import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  isAdminAuthenticated, 
  getCurrentAdmin, 
  setAdminToken, 
  removeAdminToken, 
  AdminTokenPayload 
} from '../utils/auth';

interface AuthContextType {
  isAuthenticated: boolean;
  currentAdmin: AdminTokenPayload | null;
  login: (token: string) => boolean;
  logout: () => void;
  checkAuth: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [currentAdmin, setCurrentAdmin] = useState<AdminTokenPayload | null>(null);

  const checkAuth = () => {
    const authenticated = isAdminAuthenticated();
    const admin = getCurrentAdmin();
    
    setIsAuthenticated(authenticated);
    setCurrentAdmin(admin);
  };

  const login = (token: string): boolean => {
    try {
      setAdminToken(token);
      checkAuth();
      return isAdminAuthenticated();
    } catch (error) {
      console.error('Login failed:', error);
      return false;
    }
  };

  const logout = () => {
    removeAdminToken();
    setIsAuthenticated(false);
    setCurrentAdmin(null);
  };

  // Check authentication status on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const value: AuthContextType = {
    isAuthenticated,
    currentAdmin,
    login,
    logout,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;